import React, { useState, useEffect } from "react";
import TitleUnderlined from "../components/TitleUnderlined";
import { Link, useNavigate } from "react-router-dom";
import Utils from "../tools/Utils";
import Order from "../models/Order";
import Attendee from "../models/Attendee";
import { RoutesWeb } from "../tools/RoutesWeb";
import Breadcrumb from "../components/Breadcrumb";
import APIInterface from "../tools/APIInterface";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import OrderStatus from "../models/OrderStats";
import { ShimmerText } from "../components/Shimmer";
require("../style/views/paymentView.scss");

function normalizePaypalAmount(raw) {
    const n = parseFloat(String(raw ?? "10").replace(/,/g, ""), 10);
    return (Number.isFinite(n) ? n : 10).toFixed(2);
}

const buildDebugOrder = (eventId, amountStr, mainEmail) => {
    const o = new Order();
    o.event_id = eventId;
    o.mainEmail = mainEmail;
    o.total = amountStr;
    o.attendees = [
        new Attendee({
            fullName: "Prueba PayPal (debug)",
            phoneNumber: "5555555555",
            birthDate: "1990-01-01",
            gender: "male",
            age: "35",
        }),
    ];
    return o;
};

const PaymentDebugView = () => {
    const enabled = process.env.REACT_APP_ENABLE_PAYPAL_DEBUG === "true";
    const debugAmount = normalizePaypalAmount(process.env.REACT_APP_PAYPAL_DEBUG_AMOUNT);
    const debugEmail =
        process.env.REACT_APP_PAYPAL_DEBUG_EMAIL || "debug-paypal@example.com";

    const [order, setOrder] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [orderID, setOrderID] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!enabled) {
            return;
        }
        APIInterface.event()
            .then((event) => {
                if (!event._id) {
                    setFetchError("No se pudo obtener el evento (id vacío).");
                    return;
                }
                setOrder(buildDebugOrder(event._id, debugAmount, debugEmail));
            })
            .catch((err) => {
                console.error(err);
                setFetchError(
                    "No se pudo cargar el evento. Revisa la API y REACT_APP_API_URL."
                );
            });
    }, [enabled, debugAmount, debugEmail]);

    const createOrder = (data, actions) => {
        if (!order) {
            return Promise.reject(new Error("Orden no lista"));
        }
        const value = normalizePaypalAmount(order.total);
        return actions.order
            .create({
                purchase_units: [
                    {
                        description: "Evento (debug)",
                        amount: {
                            value,
                        },
                    },
                ],
            })
            .then((id) => {
                setOrderID(id);
                return id;
            });
    };

    const onApprove = (data, actions) => {
        if (!order) {
            return Promise.reject(new Error("Orden no lista"));
        }
        const prevOrder = order;
        prevOrder.paypalId = data.orderID || orderID;
        prevOrder.total = normalizePaypalAmount(order.total);
        return APIInterface.createOrderPaypal(prevOrder)
            .then((response) => {
                if (response.status === 200) {
                    let newOrder = Order.fromJson(response.data);
                    if (newOrder.status === OrderStatus.IN_PROCESS) {
                        actions.order
                            .capture()
                            .then((details) => {
                                if (details.status === "COMPLETED") {
                                    newOrder.status = OrderStatus.PAID;
                                    newOrder.paypalId = details.id;
                                    newOrder.paypalStatus = details.status;
                                    newOrder.total = normalizePaypalAmount(order.total);
                                    APIInterface.updateOrderPaypal(newOrder)
                                        .then((res) => {
                                            const tmp = Order.fromJson(res.data);
                                            setOrder(tmp);
                                            setSuccess(true);
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                            Utils.Toast.fire(
                                                "Ups",
                                                "Ocurrió un error",
                                                "error"
                                            );
                                        });
                                }
                            })
                            .catch((error) => {
                                console.log(error);
                                Utils.Toast.fire(
                                    "Ups",
                                    "Ocurrió un error capturing",
                                    "error"
                                );
                            });
                    }
                }
            })
            .catch(() => {
                Utils.Toast.fire("Ups", "Ocurrió un error", "error");
            });
    };

    const onError = () => {
        console.error("PayPal error (debug view)");
    };

    useEffect(() => {
        if (success && order) {
            navigate(`/gracias/${Utils.encodeBase64(JSON.stringify(order))}`);
        }
    }, [success, order, navigate]);

    if (!enabled) {
        return (
            <div className="pt-5 pb-5">
                <p>
                    La vista de pago de prueba no está habilitada. Define{" "}
                    <code>REACT_APP_ENABLE_PAYPAL_DEBUG=true</code> en tu entorno y vuelve a
                    compilar.
                </p>
                <Link to={RoutesWeb.HOME}>Ir al inicio</Link>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="pt-5 pb-5">
                <p className="text-danger">{fetchError}</p>
                <Link to={RoutesWeb.HOME}>Ir al inicio</Link>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="pt-5 pb-5" id="paymentView">
                <TitleUnderlined>Pago de prueba (PayPal)</TitleUnderlined>
                <ShimmerText line={3} />
            </div>
        );
    }

    const displayTotal = parseFloat(order.total, 10);

    return (
        <div className="pt-5 pb-5" id="paymentView">
            <br />
            <br />
            <Breadcrumb
                items={[
                    { title: "Eventos", route: RoutesWeb.HOME },
                    { title: "Pago (debug)", active: true },
                ]}
            />
            <div
                className="alert alert-warning"
                role="alert"
                style={{ maxWidth: "720px" }}
            >
                <strong>Solo pruebas.</strong> Monto fijo {debugAmount} MXN. Correo de prueba:{" "}
                {debugEmail}. No uses esta ruta en producción con clientes reales.
            </div>
            <TitleUnderlined>
                Pago de prueba con <span className="embercom_overlined">PayPal</span>
            </TitleUnderlined>
            <br />
            <br />
            <br />
            <h4 className="text-uppercase">
                <b>
                    Monto de prueba:{" "}
                    <span
                        style={{
                            fontSize: "2.6rem",
                            color: "#07068E",
                            fontWeight: "bold",
                        }}
                    >
                        $ {displayTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}{" "}
                        MXN
                    </span>
                </b>
            </h4>
            <br />
            <p>
                Mismo flujo que el pago real (crear orden en API → capturar en PayPal). Si el pago
                se completa, se creará una orden real en el servidor con este monto.
            </p>
            <br />
            <PayPalScriptProvider
                options={{
                    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                    currency: "MXN",
                    "disable-funding": "card",
                }}
                onError={onError}
            >
                <PayPalButtons
                    style={{ layout: "vertical" }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                />
            </PayPalScriptProvider>
        </div>
    );
};

export default PaymentDebugView;
